import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hubspotImportApi } from '@/lib/api';

// ─── CSV Import hooks (existing) ───

export function useHubspotImports() {
  return useQuery({
    queryKey: ['hubspot-imports'],
    queryFn: () => hubspotImportApi.list(),
  });
}

export function useHubspotImport(id: string | undefined, options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: ['hubspot-imports', id],
    queryFn: () => hubspotImportApi.getById(id!),
    enabled: !!id,
    refetchInterval: options?.refetchInterval,
  });
}

export function useUploadHubspotCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: hubspotImportApi.upload,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hubspot-imports'] });
    },
  });
}

export function useHubspotImportPreview(id: string | undefined) {
  return useQuery({
    queryKey: ['hubspot-imports', id, 'preview'],
    queryFn: () => hubspotImportApi.getPreview(id!),
    enabled: !!id,
  });
}

export function useSubmitHubspotMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ importId, mappings, pipelineId }: { importId: string; mappings: Record<string, string>; pipelineId?: string }) =>
      hubspotImportApi.submitMapping(importId, mappings, pipelineId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hubspot-imports'] });
    },
  });
}

// ─── OAuth connection hooks ───

export function useHubspotOAuthStatus() {
  return useQuery({
    queryKey: ['hubspot-oauth-status'],
    queryFn: () => hubspotImportApi.getOAuthStatus(),
  });
}

export function useDisconnectHubspot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => hubspotImportApi.disconnect(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hubspot-oauth-status'] });
    },
  });
}

// ─── HubSpot API browsing hooks ───

export function useHubspotApiDeals(params?: { search?: string; after?: string; limit?: number; status?: string; pipeline?: string; dateFrom?: string; dateTo?: string }) {
  return useQuery({
    queryKey: ['hubspot-api-deals', params],
    queryFn: () => hubspotImportApi.getApiDeals(params),
    enabled: !!params,
  });
}

export function useHubspotApiPipelines(enabled = true) {
  return useQuery({
    queryKey: ['hubspot-api-pipelines'],
    queryFn: () => hubspotImportApi.getApiPipelines(),
    enabled,
  });
}

export function useImportHubspotApiDeals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { dealIds: string[]; stageMapping: Record<string, string>; pipelineId?: string }) =>
      hubspotImportApi.importApiDeals(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hubspot-imports'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

// ─── HubSpot Contacts browsing + import hooks ───

export function useHubspotApiContacts(params?: { search?: string; after?: string; limit?: number }) {
  return useQuery({
    queryKey: ['hubspot-api-contacts', params],
    queryFn: () => hubspotImportApi.getApiContacts(params),
    enabled: !!params,
  });
}

export function useImportHubspotApiContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { contactIds: string[]; importLinkedCompanies?: boolean }) =>
      hubspotImportApi.importApiContacts(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hubspot-imports'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// ─── HubSpot Companies browsing + import hooks ───

export function useHubspotApiCompanies(params?: { search?: string; after?: string; limit?: number }) {
  return useQuery({
    queryKey: ['hubspot-api-companies', params],
    queryFn: () => hubspotImportApi.getApiCompanies(params),
    enabled: !!params,
  });
}

export function useImportHubspotApiCompanies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyIds: string[] }) =>
      hubspotImportApi.importApiCompanies(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hubspot-imports'] });
      qc.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}
